
import CryptoJS from 'crypto-js';

// Input validation utilities
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^[+]?[\d\s()-]{6,20}$/;
  return phoneRegex.test(phone);
};

export const validateName = (name: string): boolean => {
  return name.length >= 1 && name.length <= 100 && /^[a-zA-ZäöåÄÖÅ\s'-]+$/.test(name);
};

export const validateLoanAmount = (amount: number): boolean => {
  return amount >= 1000 && amount <= 100000;
};

export const validateLoanTerm = (term: number): boolean => {
  return term >= 1 && term <= 120;
};

// Sanitization utilities
export const sanitizeString = (input: string): string => {
  return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
              .replace(/[<>]/g, '')
              .trim();
};

export const sanitizeEmail = (email: string): string => {
  return email.toLowerCase().trim().replace(/[^a-z0-9@._-]/g, '');
};

export const sanitizePhoneNumber = (phone: string): string => {
  return phone.replace(/[^+\d\s()-]/g, '').trim();
};

// Secure token generation
export const generateSecureToken = (): string => {
  const timestamp = Date.now().toString();
  const randomBytes = CryptoJS.lib.WordArray.random(16).toString();
  return CryptoJS.SHA256(timestamp + randomBytes).toString().substring(0, 32);
};

// Rate limiting utilities
interface RateLimitEntry {
  count: number;
  firstAttempt: number;
  lastAttempt: number;
}

const rateLimitStore = new Map<string, RateLimitEntry>();

export const checkRateLimit = (
  identifier: string, 
  maxAttempts: number = 5, 
  windowMs: number = 15 * 60 * 1000 // 15 minutes
): { allowed: boolean; remainingAttempts: number } => {
  const now = Date.now();
  const entry = rateLimitStore.get(identifier);

  if (!entry) {
    rateLimitStore.set(identifier, {
      count: 1,
      firstAttempt: now,
      lastAttempt: now
    });
    return { allowed: true, remainingAttempts: maxAttempts - 1 };
  }

  // Reset if window expired
  if (now - entry.firstAttempt > windowMs) {
    rateLimitStore.set(identifier, {
      count: 1,
      firstAttempt: now,
      lastAttempt: now
    });
    return { allowed: true, remainingAttempts: maxAttempts - 1 };
  }

  // Check if limit exceeded
  if (entry.count >= maxAttempts) {
    return { allowed: false, remainingAttempts: 0 };
  }

  // Increment counter
  entry.count++;
  entry.lastAttempt = now;
  rateLimitStore.set(identifier, entry);

  return { allowed: true, remainingAttempts: maxAttempts - entry.count };
};

// Clean up old rate limit entries
export const cleanupRateLimit = () => {
  const now = Date.now();
  const windowMs = 15 * 60 * 1000;
  
  for (const [key, entry] of rateLimitStore.entries()) {
    if (now - entry.lastAttempt > windowMs) {
      rateLimitStore.delete(key);
    }
  }
};

// Session security utilities
export const validateSession = (): boolean => {
  const sessionStart = localStorage.getItem('sessionStart');
  if (!sessionStart) return false;

  const sessionAge = Date.now() - parseInt(sessionStart);
  const maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours

  return sessionAge < maxSessionAge;
};

export const initializeSession = (): void => {
  localStorage.setItem('sessionStart', Date.now().toString());
};

export const clearSession = (): void => {
  localStorage.removeItem('sessionStart');
  localStorage.removeItem('savedApplicationData');
  localStorage.removeItem('recentApplications');
  localStorage.removeItem('redirectHistory');
};
